import { get } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import type { AccessInventoryDetail } from '@uncain/shared';
import { Link, useParams } from 'react-router';

const REFERENCE_TYPE_LABELS: Record<'secret' | 'token', string> = {
	secret: 'Secret',
	token: 'Token',
};

const SCOPE_LABELS: Record<string, string> = {
	workflow_env: 'Workflow env',
	job_env: 'Job env',
	job_with: 'Job input',
	job_secrets: 'Job secrets',
	job_if: 'Job condition',
	step_env: 'Step env',
	step_with: 'Step input',
	step_run: 'Run command',
	step_if: 'Step condition',
};

export function InventoryAccessDetailPage() {
	const params = useParams();
	const accessId = decodeURIComponent(params['*'] ?? '');
	const { data, isLoading, error } = useQuery<AccessInventoryDetail>({
		queryKey: queryKeys.inventory.accessDetail(accessId),
		queryFn: () => get<AccessInventoryDetail>(`/inventory/access/${encodeURIComponent(accessId)}`),
		enabled: accessId.length > 0,
	});

	if (isLoading) {
		return <div className="text-muted-foreground">Loading access detail...</div>;
	}

	if (error || !data) {
		return <div className="text-destructive">Failed to load access detail.</div>;
	}

	const { access, usages } = data;

	return (
		<div className="space-y-6">
			<Link to="/inventory/access" className="text-sm text-primary hover:underline">
				&larr; Back to Access
			</Link>

			<div className="space-y-2">
				<p className="text-sm uppercase tracking-[0.18em] text-muted-foreground">Inventory</p>
				<h1 className="text-2xl font-bold text-foreground">{access.displayName}</h1>
				<p className="text-sm text-muted-foreground">
					Appears in {access.repositoryCount} repositories, {access.workflowCount} workflows, and{' '}
					{access.jobCount} jobs in the active organization.
				</p>
			</div>

			<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
				<SummaryCard label="Type" value={REFERENCE_TYPE_LABELS[access.referenceType]} />
				<SummaryCard label="Usages" value={String(access.usageCount)} />
				<SummaryCard label="Workflows" value={String(access.workflowCount)} />
				<SummaryCard label="Findings" value={String(access.findingCount)} />
			</div>

			<section className="rounded-xl border border-border bg-card p-5">
				<h2 className="text-lg font-semibold text-foreground">Usage</h2>
				<div className="mt-4 space-y-3">
					{usages.map((usage) => (
						<div
							key={`${usage.jobId}-${usage.scope}-${usage.fieldName ?? 'none'}-${usage.stepIndex ?? 'none'}`}
							className="rounded-lg border border-border/70 bg-background/50 p-4"
						>
							<div className="flex flex-wrap items-center gap-2">
								<span className="font-medium text-foreground">{usage.repositoryFullName}</span>
								<span className="text-sm text-muted-foreground">{usage.workflowPath}</span>
								<span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
									{SCOPE_LABELS[usage.scope] ?? usage.scope}
								</span>
								{usage.findingCount > 0 && (
									<span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
										{usage.findingCount} finding{usage.findingCount === 1 ? '' : 's'}
									</span>
								)}
							</div>
							<p className="mt-1 text-sm text-muted-foreground">
								{usage.workflowName} · job {usage.jobKey}
								{usage.stepLabel ? ` · ${usage.stepLabel}` : ''}
							</p>
							<div className="mt-3 grid gap-3 md:grid-cols-3">
								<UsageField label="Source" value={usage.source.replaceAll('_', ' ')} />
								<UsageField label="Field" value={usage.fieldName ?? 'Unavailable'} />
								<UsageField
									label="Step Index"
									value={usage.stepIndex == null ? 'Unavailable' : String(usage.stepIndex + 1)}
								/>
							</div>
						</div>
					))}
				</div>
			</section>
		</div>
	);
}

function SummaryCard({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-border bg-card p-4">
			<p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
			<p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
		</div>
	);
}

function UsageField({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-lg border border-border/70 bg-background/60 p-3">
			<p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
			<p className={cn('mt-1 text-sm font-medium text-foreground')}>{value}</p>
		</div>
	);
}
